import { CloseButton, Group, Text, UnstyledButton } from '@mantine/core';
import { ReactElement } from 'react';

/**
 * An on/off filter pill for a working queue.
 *
 * Deliberately shares the border, radius and padding of `InboxFilterChip` so the
 * two chip families read as one system — that one is a select (label · value,
 * with a dropdown), this one is a toggle.
 *
 * The removable variant exists for a default-on chip: "Inactive hidden" is a
 * visible chip and never a silent default, because the count beside it is only
 * true given the filter, and a queue whose total quietly excludes people is how
 * a rep stops trusting the number.
 */
const CHIP_BORDER = '1px solid #dee2e6';
const CHIP_RADIUS = 8;
const CHIP_PADDING = '6px 12px';

export interface ToggleFilterChipProps {
  label: string;
  active: boolean;
  onToggle: () => void;
  /** When given, the chip can be dismissed entirely rather than just switched off. */
  onRemove?: () => void;
}

export function ToggleFilterChip({ label, active, onToggle, onRemove }: ToggleFilterChipProps): ReactElement {
  return (
    <Group
      gap={6}
      wrap="nowrap"
      style={{
        border: CHIP_BORDER,
        borderRadius: CHIP_RADIUS,
        padding: CHIP_PADDING,
        backgroundColor: active ? 'var(--mantine-color-gray-1)' : undefined,
      }}
    >
      <UnstyledButton aria-label={label} aria-pressed={active} onClick={onToggle}>
        <Text size="sm" fw={active ? 600 : 400}>
          {label}
        </Text>
      </UnstyledButton>
      {onRemove ? (
        <CloseButton
          size="xs"
          aria-label={`Remove ${label} filter`}
          onClick={(event) => {
            // Keeps a dismiss from also reaching an ancestor click handler —
            // the chip bar may sit inside one. Covered by a regression test.
            event.stopPropagation();
            onRemove();
          }}
        />
      ) : null}
    </Group>
  );
}
